import React, { ChangeEvent, FC, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup,
    Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from '@mui/lab';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import Loader from 'components/shared/loader/loader.component';
import InfoText from 'components/shared/info-text.component';
import ApprovedByAutocomplete from 'components/orders/order-details/approved-by-autocomplete/approved-by-autocomplete.component';
import StatusDatePicker from 'components/orders/order-details/status-date-picker.component';
import BatchSkippedOrders from 'components/orders/shared/batch-skipped-orders.component';
import { batchSendOrdersPreviewTemplate, batchSendOrdersTemplate } from 'apollo/templates/order';
import { BatchOrdersResult, BatchPerOrderInvoiceAction, BatchSendOrdersPreview, InvoicingState } from 'models/order';
import { User } from 'models/user';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';

type BatchSendToClinicDialogProps = {
    open: boolean;
    orderIds: string[];
    onClose: () => void;
    onDone: (result: BatchOrdersResult) => void;
};

/**
 * DD-3463: the "Send the order" dialog for a whole batch. One approver, date and
 * archive choice for every order; production is always marked finished; transport
 * is never set. The selected orders are split into up to three invoicing sections.
 */
const BatchSendToClinicDialog: FC<BatchSendToClinicDialogProps> = ({
    open,
    orderIds,
    onClose,
    onDone,
}: BatchSendToClinicDialogProps) => {
    const { t: translate } = useTranslation(['common', 'orders', 'invoices']);
    const { showBoundary } = useDenthubErrorBoundary();
    const [labTechnician, setLabTechnician] = useState<User | null>(null);
    const [date, setDate] = useState<Date | null>(new Date());
    const [isArchive, setIsArchive] = useState(false);
    // Prefilled: one PDF with every delivery note of the batch. The user can opt out.
    const [compileDeliveryNotes, setCompileDeliveryNotes] = useState(true);
    const [collectiveInvoiceAction, setCollectiveInvoiceAction] = useState<InvoicingState>(
        InvoicingState.READY_FOR_INVOICE,
    );
    const [perOrderInvoiceAction, setPerOrderInvoiceAction] = useState<BatchPerOrderInvoiceAction>(
        BatchPerOrderInvoiceAction.CREATE_INVOICE,
    );

    const { data, loading } = useQuery<{ batchSendOrdersPreview: BatchSendOrdersPreview }>(
        batchSendOrdersPreviewTemplate,
        {
            variables: { orderIds },
            fetchPolicy: 'network-only',
            skip: !open,
            onError(error) {
                showBoundary(error);
            },
        },
    );
    const preview = data?.batchSendOrdersPreview;

    const counts = useMemo(() => {
        const byGroup = { COLLECTIVE: 0, PER_ORDER: 0, NO_INVOICE: 0 };
        preview?.eligible.forEach((order) => {
            byGroup[order.invoiceGroup] += 1;
        });
        return byGroup;
    }, [preview]);
    const eligibleCount = preview?.eligible.length ?? 0;

    const [batchSendOrders, { loading: running }] = useMutation<{ batchSendOrders: BatchOrdersResult }>(
        batchSendOrdersTemplate,
        {
            onCompleted: ({ batchSendOrders: result }) => {
                onDone(result);
            },
            onError(error) {
                showBoundary(error);
            },
        },
    );

    const handleConfirm = () => {
        if (!preview || !labTechnician) {
            return;
        }
        batchSendOrders({
            variables: {
                input: {
                    orderIds: preview.eligible.map((order) => order._id),
                    approvedBy: { fullName: labTechnician.fullName, id: labTechnician._id || null },
                    date,
                    isArchive,
                    collectiveInvoiceAction,
                    perOrderInvoiceAction: preview.isPerOrderInvoiceDisabled
                        ? BatchPerOrderInvoiceAction.DO_NOTHING
                        : perOrderInvoiceAction,
                    compileDeliveryNotes,
                },
            },
        });
    };

    const isFormInvalid = !labTechnician?.fullName || !eligibleCount;

    return (
        <DHDialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            title={translate('orders:BATCH_SEND_TO_CLINIC')}
            actions={
                <>
                    {eligibleCount ? (
                        <FormControl margin="dense" fullWidth>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="secondary"
                                        checked={isArchive}
                                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                            setIsArchive(event.target.checked)
                                        }
                                    />
                                }
                                label={translate('orders:ARCHIVE_THE_ORDERS')}
                                data-test="batch-send-to-clinic-dialog.archive-checkbox"
                            />
                        </FormControl>
                    ) : undefined}
                    <Button onClick={onClose}>{translate('common:CANCEL')}</Button>
                    {eligibleCount ? (
                        <LoadingButton
                            sx={{ minWidth: 220 }}
                            variant="contained"
                            disabled={isFormInvalid}
                            onClick={handleConfirm}
                            loading={running}
                            data-test="batch-send-to-clinic-dialog.confirm"
                        >
                            {translate('orders:BATCH_SEND_ORDERS_BUTTON', { count: eligibleCount })}
                        </LoadingButton>
                    ) : undefined}
                </>
            }
        >
            {loading || !preview ? (
                <Loader />
            ) : !eligibleCount ? (
                <Box>
                    <Alert severity="info">{translate('orders:BATCH_NO_ELIGIBLE_ORDERS')}</Alert>
                    <BatchSkippedOrders skipped={preview.skipped} />
                </Box>
            ) : (
                <Box>
                    <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
                        {translate('orders:BATCH_SEND_TO_CLINIC_INFO', { count: eligibleCount })}
                    </Alert>
                    <BatchSkippedOrders skipped={preview.skipped} />

                    <Box mt={2}>
                        <Typography variant="h4" mb={1}>
                            {translate('orders:APPROVED_BY')}
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={5}>
                                <ApprovedByAutocomplete
                                    labTechnician={labTechnician}
                                    onTechnicianSelected={setLabTechnician}
                                    dataTest="batch-send-to-clinic-dialog.approved-by-autocomplete"
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <StatusDatePicker onChangeDate={setDate} />
                            </Grid>
                        </Grid>
                        <InfoText text={translate('orders:BATCH_APPROVED_BY_INSTRUCTIONS')} />
                    </Box>

                    <Box mt={2}>
                        <Typography variant="h4">{translate('orders:INVOICE_STATE')}</Typography>

                        {counts.COLLECTIVE ? (
                            <Box mt={1} data-test="batch-send-to-clinic-dialog.collective-section">
                                <Typography variant="subtitle2">
                                    {translate('orders:BATCH_ORDERS_WITH_COLLECTIVE_INVOICE', {
                                        count: counts.COLLECTIVE,
                                    })}
                                </Typography>
                                <FormControl component="fieldset">
                                    <RadioGroup
                                        row
                                        value={collectiveInvoiceAction}
                                        onChange={(event) =>
                                            setCollectiveInvoiceAction(event.target.value as InvoicingState)
                                        }
                                    >
                                        <FormControlLabel
                                            value={InvoicingState.NOT_READY}
                                            control={<Radio color="secondary" />}
                                            label={translate('orders:NOT_READY')}
                                        />
                                        <FormControlLabel
                                            value={InvoicingState.READY_FOR_INVOICE}
                                            control={<Radio color="secondary" />}
                                            label={translate('orders:READY_FOR_INVOICE')}
                                        />
                                        <FormControlLabel
                                            value={InvoicingState.SHOULD_NOT_INVOICE}
                                            control={<Radio color="secondary" />}
                                            label={translate('orders:DO_NOT_INVOICE')}
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Box>
                        ) : undefined}

                        {counts.PER_ORDER ? (
                            <Box mt={1} data-test="batch-send-to-clinic-dialog.per-order-section">
                                <Typography variant="subtitle2">
                                    {translate('orders:BATCH_ORDERS_WITH_PER_ORDER_INVOICE', {
                                        count: counts.PER_ORDER,
                                    })}
                                </Typography>
                                <FormControl component="fieldset" disabled={preview.isPerOrderInvoiceDisabled}>
                                    <RadioGroup
                                        row
                                        value={
                                            preview.isPerOrderInvoiceDisabled
                                                ? BatchPerOrderInvoiceAction.DO_NOTHING
                                                : perOrderInvoiceAction
                                        }
                                        onChange={(event) =>
                                            setPerOrderInvoiceAction(event.target.value as BatchPerOrderInvoiceAction)
                                        }
                                    >
                                        <FormControlLabel
                                            value={BatchPerOrderInvoiceAction.DO_NOTHING}
                                            control={<Radio color="secondary" />}
                                            label={translate('orders:INVOICE_DO_NOTHING')}
                                        />
                                        <FormControlLabel
                                            value={BatchPerOrderInvoiceAction.CREATE_INVOICE}
                                            control={<Radio color="secondary" />}
                                            label={translate('invoices:CREATE_INVOICE')}
                                        />
                                    </RadioGroup>
                                </FormControl>
                                {preview.isPerOrderInvoiceDisabled ? (
                                    <Alert severity="info" variant="outlined" sx={{ border: 0 }}>
                                        {translate('orders:BATCH_PER_ORDER_INVOICE_DISABLED')}
                                    </Alert>
                                ) : undefined}
                            </Box>
                        ) : undefined}

                        {counts.NO_INVOICE ? (
                            <Box mt={1} data-test="batch-send-to-clinic-dialog.no-invoice-section">
                                <Alert severity="info" variant="outlined" sx={{ border: 0 }}>
                                    {translate('orders:BATCH_ORDERS_NOT_INVOICED', { count: counts.NO_INVOICE })}
                                </Alert>
                            </Box>
                        ) : undefined}
                    </Box>

                    <Box mt={2}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="secondary"
                                    checked={compileDeliveryNotes}
                                    onChange={(event) => setCompileDeliveryNotes(event.target.checked)}
                                    data-test="batch-send-to-clinic-dialog.compile-pdf-checkbox"
                                />
                            }
                            label={translate('orders:CREATE_COMPILED_DELIVERY_NOTES_PDF')}
                        />
                    </Box>
                </Box>
            )}
        </DHDialog>
    );
};

export default BatchSendToClinicDialog;
